import { useCallback, useEffect, useState } from 'react'
import {
  adminArchiveIssue,
  adminDeleteIssue,
  adminRankIssue,
  adminSetIssueStatus,
  adminUpdateIssue,
  createIssue,
  DEPARTMENTS,
  updateIssueText,
  ISSUE_STATUSES,
  ISSUE_STATUS_TONE,
  listIssues,
  URGENCIES,
  type Issue,
  type IssueStatus,
  type Urgency,
} from '../lib/issuesApi'
import IssueCard from './IssueCard'
import HeaderAction from './HeaderAction'

/**
 * Handed over by "Create an issue" — from the Headlines tab (a headline) or a
 * to-do (the Ninety "linked issue"). At most one of the two links is set.
 */
export interface IssuePrefill {
  headlineId: string | null
  taskId: string | null
  /** The to-do's title, shown on the form so the reporter knows what it links to. */
  taskTitle?: string | null
  description: string
}

interface Props {
  token: string
  isAdmin: boolean
  /** True when the QR code was scanned — open straight onto the report form. */
  startNew: boolean
  onStartNewHandled: () => void
  /** Opens the report form already filled in from a headline. */
  prefill: IssuePrefill | null
  onPrefillUsed: () => void
  /** Admin only: hand an issue to the task form, pre-filled and linked. */
  onMakeTask: (issue: Issue) => void
  /** Desktop row layout for the cards — see IssueCard. */
  compact?: boolean
  /** Bump to make the list re-fetch (Meeting mode, when the presenter acts). */
  refreshKey?: number
  /** Meeting mode: IDS works the open queue, so the Solved fold is noise there. */
  hideSolved?: boolean
  /** Fired when the report form closes (submitted or cancelled). */
  onFormClosed?: () => void
}

type Mode =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'triage'; issue: Issue }
  | { kind: 'outcome'; issue: Issue }
  | { kind: 'edit'; issue: Issue }

interface Draft {
  description: string
  urgency: Urgency
  department: string
  occurredAt: string
  impact: string
  proposedSolution: string
  followUpRequired: boolean
  /** Only set when the issue was dropped down from a headline. */
  headlineId: string | null
  /** Only set when the issue is being raised from a to-do. */
  taskId: string | null
  taskTitle: string | null
}

const EMPTY: Draft = {
  description: '',
  urgency: 'Medium',
  department: 'Operations',
  occurredAt: '',
  impact: '',
  proposedSolution: '',
  followUpRequired: false,
  headlineId: null,
  taskId: null,
  taskTitle: null,
}

const trimmedOrNull = (s: string) => (s.trim() === '' ? null : s.trim())

export default function Issues({
  token,
  isAdmin,
  startNew,
  onStartNewHandled,
  prefill,
  onPrefillUsed,
  onMakeTask,
  compact = false,
  refreshKey = 0,
  hideSolved = false,
  onFormClosed,
}: Props) {
  const [issues, setIssues] = useState<Issue[] | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [solvedOpen, setSolvedOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const load = useCallback(async () => {
    try {
      setIssues(await listIssues(token, showArchived))
    } catch (err) {
      setIssues([])
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token, showArchived])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  // The QR code in the shop points at `…/#new-issue`, so scanning it and
  // signing in should land on the form itself, not on a list with a button.
  useEffect(() => {
    if (!startNew) return
    setDraft(EMPTY)
    setDetailOpen(false)
    setMode({ kind: 'new' })
    onStartNewHandled()
  }, [startNew, onStartNewHandled])

  // Arriving from Headlines → "Create an issue". The headline's own words seed
  // the description, and the link is carried through so create_issue can both
  // record where it came from and mark the headline shared.
  useEffect(() => {
    if (!prefill) return
    setDraft({
      ...EMPTY,
      description: prefill.description,
      headlineId: prefill.headlineId,
      taskId: prefill.taskId,
      taskTitle: prefill.taskTitle ?? null,
    })
    setDetailOpen(false)
    setMode({ kind: 'new' })
    onPrefillUsed()
  }, [prefill, onPrefillUsed])

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true)
    setProblem(null)
    try {
      await fn()
      await load()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const submit = () =>
    guard(async () => {
      await createIssue(token, {
        description: draft.description,
        urgency: draft.urgency,
        // The form no longer asks where (see DEPARTMENTS in issuesApi.ts).
        location: null,
        department: trimmedOrNull(draft.department),
        // Sent as the raw `YYYY-MM-DDTHH:MM` string. create_issue attaches
        // America/New_York — see the note in issuesApi.ts.
        occurredAt: trimmedOrNull(draft.occurredAt),
        impact: trimmedOrNull(draft.impact),
        proposedSolution: trimmedOrNull(draft.proposedSolution),
        followUpRequired: draft.followUpRequired,
        headlineId: draft.headlineId,
        taskId: draft.taskId,
      })
      setDraft(EMPTY)
      setDetailOpen(false)
      setMode({ kind: 'list' })
      // Kept short on purpose (Scott, 2026-10-22). It also promises nothing about
      // who was alerted: an admin's own issue may send no alert at all (0019).
      setDone('Submission successful.')
      onFormClosed?.()
    })

  // ---------------------------------------------------------------- report form
  if (mode.kind === 'new') {
    const valid = draft.description.trim() !== ''

    return (
      <section className="form">
        <h2 className="form__title">Report an issue</h2>
        <p className="form__lead muted">
          {draft.headlineId
            ? 'Dropped down from a headline. Edit it into something the team can act on — the headline stays linked, and is marked shared when you submit.'
            : draft.taskId
              ? `Raised from the to-do “${draft.taskTitle ?? 'this task'}”. Say what went wrong or what you want to bring up — the issue stays linked to the to-do, so it is waiting in IDS and says where it came from.`
              : 'It goes straight to the managers. Your name is on it — everyone in the shop can see the list.'}
        </p>

        {problem && <p className="banner">{problem}</p>}

        <label className="field">
          <span className="field__label">What happened?</span>
          <textarea
            className="field__input"
            rows={4}
            autoFocus
            value={draft.description}
            placeholder={
              draft.taskId
                ? 'e.g. The parts for this keep arriving wrong'
                : 'e.g. Lift 3 is dropping about an inch overnight'
            }
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <span className="field__hint">
            One line is enough for a meeting topic — it becomes the headline in the
            list. The detail below is optional.
          </span>
        </label>

        <div className="field">
          <span className="field__label">How urgent?</span>
          <div className="seg" role="group" aria-label="Urgency">
            {URGENCIES.map((u) => (
              <button
                key={u}
                type="button"
                className={'seg__btn' + (draft.urgency === u ? ' seg__btn--on' : '')}
                aria-pressed={draft.urgency === u}
                onClick={() => setDraft({ ...draft, urgency: u })}
              >
                {u}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label">Department</span>
          <div className="picks">
            {DEPARTMENTS.map((d) => (
              <button
                key={d}
                type="button"
                className={'picks__btn' + (draft.department === d ? ' picks__btn--on' : '')}
                aria-pressed={draft.department === d}
                onClick={() => setDraft({ ...draft, department: d })}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Everything the Google Form asked that most reports do not need.
            Folded away so the common case is three controls, not nine. */}
        <div className="fold">
          <button
            type="button"
            className="fold__head"
            aria-expanded={detailOpen}
            onClick={() => setDetailOpen((o) => !o)}
          >
            <span className="fold__caret" aria-hidden="true">
              {detailOpen ? '▾' : '▸'}
            </span>
            Add more detail
          </button>

          {detailOpen && (
            <div className="fold__body">
              <label className="field">
                <span className="field__label">When did it happen?</span>
                <input
                  className="field__input"
                  type="datetime-local"
                  value={draft.occurredAt}
                  onChange={(e) => setDraft({ ...draft, occurredAt: e.target.value })}
                />
              </label>

              <label className="field">
                <span className="field__label">What is it costing us?</span>
                <textarea
                  className="field__input"
                  rows={2}
                  value={draft.impact}
                  placeholder="e.g. Bay 3 is out, so we are turning work away"
                  onChange={(e) => setDraft({ ...draft, impact: e.target.value })}
                />
              </label>

              <label className="field">
                <span className="field__label">Any suggested fix?</span>
                <textarea
                  className="field__input"
                  rows={2}
                  value={draft.proposedSolution}
                  placeholder="What would you do about it?"
                  onChange={(e) =>
                    setDraft({ ...draft, proposedSolution: e.target.value })
                  }
                />
              </label>

              <label className="check">
                <input
                  type="checkbox"
                  checked={draft.followUpRequired}
                  onChange={(e) =>
                    setDraft({ ...draft, followUpRequired: e.target.checked })
                  }
                />
                <span>I would like someone to come back to me about this</span>
              </label>
            </div>
          )}
        </div>

        <div className="form__actions">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setMode({ kind: 'list' })
              setProblem(null)
              onFormClosed?.()
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !valid}
            onClick={() => void submit()}
          >
            {busy ? 'Sending…' : 'Report it'}
          </button>
        </div>
        {!valid && (
          <p className="muted form__hint">Say what happened and you are done.</p>
        )}
      </section>
    )
  }

  // ------------------------------------------------------------- triage form
  if (mode.kind === 'triage') {
    return (
      <TriageForm
        issue={mode.issue}
        busy={busy}
        problem={problem}
        onCancel={() => {
          setMode({ kind: 'list' })
          setProblem(null)
        }}
        onSave={(fields) =>
          void guard(async () => {
            await adminUpdateIssue(token, mode.issue.id, fields)
            setMode({ kind: 'list' })
          })
        }
      />
    )
  }

  // --------------------------------------------------------------- edit form
  if (mode.kind === 'edit') {
    return (
      <EditTextForm
        issue={mode.issue}
        busy={busy}
        problem={problem}
        onCancel={() => {
          setMode({ kind: 'list' })
          setProblem(null)
        }}
        onSave={(fields) =>
          void guard(async () => {
            await updateIssueText(token, mode.issue.id, fields)
            setMode({ kind: 'list' })
          })
        }
      />
    )
  }

  // ------------------------------------------------------------ outcome form
  if (mode.kind === 'outcome') {
    return (
      <OutcomeForm
        issue={mode.issue}
        busy={busy}
        problem={problem}
        onCancel={() => {
          setMode({ kind: 'list' })
          setProblem(null)
        }}
        onSave={(notes, status) =>
          void guard(async () => {
            await adminSetIssueStatus(token, mode.issue.id, status, notes)
            setMode({ kind: 'list' })
          })
        }
      />
    )
  }

  // -------------------------------------------------------------------- list
  if (issues === null) return <p className="muted">Loading…</p>

  const cardFor = (issue: Issue) => (
    <IssueCard
      key={issue.id}
      issue={issue}
      token={token}
      isAdmin={isAdmin}
      busy={busy}
      expanded={expanded === issue.id}
      onToggle={() => setExpanded((id) => (id === issue.id ? null : issue.id))}
      onStatus={(i, s) =>
        void guard(async () => {
          await adminSetIssueStatus(token, i.id, s, null)
        })
      }
      onArchive={(i) =>
        void guard(async () => {
          await adminArchiveIssue(token, i.id, !i.archived)
        })
      }
      onDelete={(i) =>
        guard(async () => {
          await adminDeleteIssue(token, i.id)
        })
      }
      onTriage={(i) => setMode({ kind: 'triage', issue: i })}
      onResolution={(i) => setMode({ kind: 'outcome', issue: i })}
      onMakeTask={onMakeTask}
      onRank={(i, r) =>
        void guard(async () => {
          await adminRankIssue(token, i.id, r)
        })
      }
      onEditText={(i) => setMode({ kind: 'edit', issue: i })}
      onLinksChanged={() => void load()}
      compact={compact}
    />
  )

  // Ranked issues lead, in rank order — the meeting works them top-down.
  // The server already sorts them first; this pulls them under their own
  // label so the Identified/Discussing groups below are what is NOT yet
  // picked. The server clears a rank on Solved/archive, so nothing here
  // needs to second-guess that.
  const ranked = issues
    .filter((i) => i.rank != null && i.status !== 'Solved' && !i.archived)
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
  const rankedIds = new Set(ranked.map((i) => i.id))
  const byStatus = (s: IssueStatus) =>
    issues.filter((i) => i.status === s && !rankedIds.has(i.id))
  const open = ISSUE_STATUSES.filter((s) => s !== 'Solved')
  const solved = byStatus('Solved')

  return (
    <section>
      <HeaderAction>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setDraft(EMPTY)
            setDetailOpen(false)
            setDone(null)
            setMode({ kind: 'new' })
          }}
        >
          + Report an issue
        </button>
      </HeaderAction>

      {done && <p className="banner banner--ok">{done}</p>}
      {problem && <p className="banner">{problem}</p>}

      {isAdmin && (
        <label className="check check--inline">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span className="muted">Show archived</span>
        </label>
      )}

      {issues.length === 0 && (
        <div className="panel panel--flat">
          <p className="muted">
            Nothing reported yet. If something in the shop is not right, say so above —
            it reaches the managers straight away.
          </p>
        </div>
      )}

      {ranked.length > 0 && (
        <section className="group">
          <h2 className="group__label group__label--today">
            Ranked for the meeting
            <span className="group__count">{ranked.length}</span>
          </h2>
          {ranked.map(cardFor)}
        </section>
      )}

      {open.map((status) => {
        const rows = byStatus(status)
        if (rows.length === 0) return null
        return (
          <section key={status} className="group">
            <h2
              className={
                'group__label' + (status === 'Identified' ? ' group__label--today' : '')
              }
            >
              {status}
              <span className="group__count">{rows.length}</span>
            </h2>
            {rows.map(cardFor)}
          </section>
        )
      })}

      {/* Solved is folded away. Unlike tasks these are never archived on a
          schedule, so left open the list would be mostly history within months. */}
      {!hideSolved && solved.length > 0 && (
        <div className="fold">
          <button
            type="button"
            className="fold__head"
            aria-expanded={solvedOpen}
            onClick={() => setSolvedOpen((o) => !o)}
          >
            <span className="fold__caret" aria-hidden="true">
              {solvedOpen ? '▾' : '▸'}
            </span>
            Solved
            <span className="group__count">{solved.length}</span>
          </button>
          {solvedOpen && <div className="fold__body">{solved.map(cardFor)}</div>}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------

interface TriageFields {
  title: string
  department: string
  location: string | null
  urgency: Urgency
  followUpRequired: boolean
}

function TriageForm({
  issue,
  busy,
  problem,
  onCancel,
  onSave,
}: {
  issue: Issue
  busy: boolean
  problem: string | null
  onCancel: () => void
  onSave: (fields: TriageFields) => void
}) {
  const [title, setTitle] = useState(issue.title)
  const [department, setDepartment] = useState(issue.department)
  const [urgency, setUrgency] = useState<Urgency>(issue.urgency)
  const [followUp, setFollowUp] = useState(issue.follow_up_required)

  return (
    <section className="form">
      <h2 className="form__title">Reclassify</h2>
      <p className="form__lead muted">
        Tidy the headline and put it in the right box. What {issue.reported_by_name}{' '}
        actually wrote is theirs and cannot be edited here.
      </p>

      {problem && <p className="banner">{problem}</p>}

      <label className="field">
        <span className="field__label">Headline</span>
        <input
          className="field__input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>

      <div className="field">
        <span className="field__label">Urgency</span>
        <div className="seg" role="group" aria-label="Urgency">
          {URGENCIES.map((u) => (
            <button
              key={u}
              type="button"
              className={'seg__btn' + (urgency === u ? ' seg__btn--on' : '')}
              aria-pressed={urgency === u}
              onClick={() => setUrgency(u)}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span className="field__label">Department</span>
        <div className="picks">
          {DEPARTMENTS.map((d) => (
            <button
              key={d}
              type="button"
              className={'picks__btn' + (department === d ? ' picks__btn--on' : '')}
              aria-pressed={department === d}
              onClick={() => setDepartment(d)}
            >
              {d}
            </button>
          ))}
        </div>
        {/* An older issue can carry a retired value ("Shop"); say so rather
            than show no chip selected with no explanation. */}
        {!(DEPARTMENTS as readonly string[]).includes(department) && (
          <span className="field__hint">
            Currently filed under “{department}”, which is no longer a choice. Pick one
            to move it.
          </span>
        )}
      </div>

      <label className="check">
        <input
          type="checkbox"
          checked={followUp}
          onChange={(e) => setFollowUp(e.target.checked)}
        />
        <span>Needs following up with the reporter</span>
      </label>

      <div className="form__actions">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || title.trim() === ''}
          onClick={() =>
            onSave({
              title,
              department,
              // No longer asked; keep whatever an older issue already has.
              location: issue.location,
              urgency,
              followUpRequired: followUp,
            })
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------

function OutcomeForm({
  issue,
  busy,
  problem,
  onCancel,
  onSave,
}: {
  issue: Issue
  busy: boolean
  problem: string | null
  onCancel: () => void
  onSave: (notes: string, status: IssueStatus) => void
}) {
  const [notes, setNotes] = useState(issue.resolution_notes ?? '')
  // Writing the outcome is nearly always the moment you close it, so default to
  // Solved regardless of where it was — but leave it changeable, because "we
  // discussed it and parked it" is a real outcome too.
  const [status, setStatus] = useState<IssueStatus>('Solved')

  return (
    <section className="form">
      <h2 className="form__title">What happened about it?</h2>
      <p className="form__lead muted">{issue.title}</p>

      {problem && <p className="banner">{problem}</p>}

      <label className="field">
        <span className="field__label">Outcome</span>
        <textarea
          className="field__input"
          rows={3}
          autoFocus
          value={notes}
          placeholder="e.g. Called Rotary out, seal replaced 8/22"
          onChange={(e) => setNotes(e.target.value)}
        />
        <span className="field__hint">
          Everyone can read this. It is the record of what the shop decided.
        </span>
      </label>

      <div className="field">
        <span className="field__label">Set status to</span>
        <div className="seg" role="group" aria-label="Status">
          {ISSUE_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={
                'seg__btn' +
                (status === s ? ` seg__btn--on seg__btn--${ISSUE_STATUS_TONE[s]}` : '')
              }
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="form__actions">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => onSave(notes, status)}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------

/**
 * Fixing what you wrote.
 *
 * Only the three fields that are the reporter's own account: description,
 * impact, proposed solution. Classification lives in Reclassify and status in
 * the outcome form, so the two never overlap and an admin tidying a title
 * cannot accidentally rewrite somebody's words at the same time.
 */
function EditTextForm({
  issue,
  busy,
  problem,
  onCancel,
  onSave,
}: {
  issue: Issue
  busy: boolean
  problem: string | null
  onCancel: () => void
  onSave: (fields: {
    description: string
    impact: string | null
    proposedSolution: string | null
  }) => void
}) {
  const [description, setDescription] = useState(issue.description)
  const [impact, setImpact] = useState(issue.impact ?? '')
  const [solution, setSolution] = useState(issue.proposed_solution ?? '')

  return (
    <section className="form">
      <h2 className="form__title">Edit this issue</h2>
      <p className="form__lead muted">
        {issue.mine
          ? 'Fix anything you did not get right the first time.'
          : `Correcting what ${issue.reported_by_name} wrote. The original is kept in the record.`}
      </p>

      {problem && <p className="banner">{problem}</p>}

      <label className="field">
        <span className="field__label">What happened?</span>
        <textarea
          className="field__input"
          rows={4}
          autoFocus
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <span className="field__hint">
          The headline in the list follows the first line, unless a manager has
          already given it one of its own.
        </span>
      </label>

      <label className="field">
        <span className="field__label">What is it costing us?</span>
        <textarea
          className="field__input"
          rows={2}
          value={impact}
          onChange={(e) => setImpact(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Any suggested fix?</span>
        <textarea
          className="field__input"
          rows={2}
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
        />
      </label>

      <div className="form__actions">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || description.trim() === ''}
          onClick={() =>
            onSave({
              description,
              impact: impact.trim() === '' ? null : impact.trim(),
              proposedSolution: solution.trim() === '' ? null : solution.trim(),
            })
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  )
}
